import { useT } from '../hooks/useT';

type Props = {
  /** Optional per-tenant imprint hint (server-sanitized HTML). */
  imprintHtml?: string | null | undefined;
};

/**
 * ISSUE-0101: an optional collapse marker placed inside the imprint splits it — everything before
 * the marker renders inline (short "footer" instructions), everything after collapses into a
 * default-hidden <details> so a full Impressum block doesn't dominate the page. The marker is an
 * HTML comment, which survives the server-side MailHtmlSanitizer (it strips scripts/handlers, not
 * comments). Canonical form: `<!-- collapse from here / default: hide -->`; any `<!-- collapse … -->`
 * comment is accepted.
 */
const COLLAPSE_MARKER = /<!--\s*collapse\b[^>]*-->/i;

function splitImprint(html: string): { head: string; tail: string | null } {
  const m = COLLAPSE_MARKER.exec(html);
  if (!m) return { head: html, tail: null };
  const tail = html.slice(m.index + m[0].length).trim();
  return { head: html.slice(0, m.index), tail: tail.length > 0 ? tail : null };
}

/**
 * Footer rendered on the booking form: the sending tenant's imprint hint, when present.
 * ISSUE-0165: the platform legal-nav links were removed; the legal routes remain reachable directly
 * (e.g. the CookieBanner deep-links to Datenschutz). Renders nothing when there is no imprint.
 */
export function LegalFooter({ imprintHtml }: Props) {
  const { t } = useT();
  if (!imprintHtml) return null;
  const imprint = splitImprint(imprintHtml);

  return (
    <footer className="mx-auto max-w-2xl px-4 pb-8 pt-2 text-sm text-slate-500">
      <div>
        {imprint.head.trim() && (
          <div className="text-slate-600" dangerouslySetInnerHTML={{ __html: imprint.head }} />
        )}
        {imprint.tail && (
          <details className="mt-2">
            <summary className="cursor-pointer text-xs font-medium text-slate-500 hover:text-slate-700">
              {t('offer.imprint')}
            </summary>
            <div className="mt-2 text-slate-600" dangerouslySetInnerHTML={{ __html: imprint.tail }} />
          </details>
        )}
      </div>
    </footer>
  );
}
